






















































import { Component, Ref, Vue, Watch } from "vue-property-decorator";
import BaseDialogComponent from "@/components/common/BaseDialog.vue";
import ConfirmationDialogComponent from "@/components/common/ConfirmationDialog.vue";
import { ClientPartner, PartnerChangeGroup } from "@/data/models/ClientPartner";
import { updateClientPartner } from "@/data/repository/ClientAdministrationRepository";
import { SnackbarModule } from "@/store/modules/SnackbarModule";
import {
  PARTNER_STATUS_ORDER,
  PartnerFieldSpec,
  isValidPartnerAddress,
  isValidPartnerEndpoint,
  openGoLiveChecks,
  partnerChanges,
  partnerErrorKey,
  partnerFieldSpecs,
  partnerForm,
} from "@/utils/ClientPartners";

/** What `show()` resolves with: the partner after the save, CHANGED_MEANWHILE (the page reloads), or null. */
export type PartnerDialogResult = ClientPartner | "CHANGED_MEANWHILE" | null;

const PARTNERS = "admin.clients.partners";

/**
 * Edits one card of a partner's page (Status, Verbindung, Was ... bekommt, Verhalten): only the fields
 * that changed are sent, each with the value shown, so IRES Cloud can refuse one changed meanwhile.
 */
@Component({
  components: {
    "base-dialog": BaseDialogComponent,
    "confirmation-dialog": ConfirmationDialogComponent,
  },
})
export default class ClientPartnerFieldsDialog extends Vue {
  @Ref("baseDialog")
  baseDialog!: BaseDialogComponent;

  @Ref("confirm")
  confirmDialog!: ConfirmationDialogComponent;

  busy = false;
  clientId = "";
  group: PartnerChangeGroup = "PARTNER_STATUS";
  // Initialised, not `!`: the computeds below must track them from the first render (edit-mode-dialogs.md).
  partner: ClientPartner | null = null;
  form: Record<string, unknown> = {};

  private resolve: ((result: PartnerDialogResult) => void) | null = null;

  get specs(): PartnerFieldSpec[] {
    return this.partner ? partnerFieldSpecs(this.group, this.partner) : [];
  }

  get textAndStatusSpecs(): PartnerFieldSpec[] {
    return this.specs.filter((spec) => spec.kind !== "switch");
  }

  get switchSpecs(): PartnerFieldSpec[] {
    return this.specs.filter((spec) => spec.kind === "switch");
  }

  get onlySwitches(): boolean {
    return this.textAndStatusSpecs.length === 0;
  }

  get statusItems() {
    return PARTNER_STATUS_ORDER.map((value) => ({ value, label: this.$t(`${PARTNERS}.statuses.${value}`).toString() }));
  }

  get changes() {
    // Read the form first, so the computed tracks it even before a partner is shown (ui-conventions.md).
    const form = { ...this.form };
    return this.partner ? partnerChanges(this.group, this.partner, form) : {};
  }

  get hasChanged(): boolean {
    return Object.keys(this.changes).length > 0;
  }

  get hasErrors(): boolean {
    return this.textAndStatusSpecs.some((spec) => this.errorOf(spec.field) !== "");
  }

  get canSave(): boolean {
    return this.hasChanged && !this.hasErrors;
  }

  /** What the change reaches beyond the screen: pausing, resuming, the swap switched off. */
  get warnings(): string[] {
    const changes = this.changes;
    const warnings: string[] = [];
    if (changes.integrationStatus?.value === "PAUSED") {
      warnings.push(`${PARTNERS}.warnings.pause`);
    }
    if (changes.integrationStatus && changes.integrationStatus.expected === "PAUSED") {
      warnings.push(`${PARTNERS}.warnings.resume`);
    }
    if (changes.appointmentFieldSwap?.value === false) {
      warnings.push(`${PARTNERS}.warnings.swapOff`);
    }
    if (changes.acceptsProjectStatusUpdates?.value === false) {
      warnings.push(`${PARTNERS}.warnings.noStatus`);
    }
    return warnings;
  }

  /** Choosing Live names the go-live items still open; it does not stop the save. */
  get goLiveWarning(): string {
    const open = this.changes.integrationStatus?.value === "LIVE" ? openGoLiveChecks(this.partner?.goLive) : [];
    if (open.length === 0) {
      return "";
    }
    const items = open.map((check) => this.$t(`${PARTNERS}.goLive.checks.${check.key}`).toString()).join(", ");
    return this.$t(`${PARTNERS}.goLive.openWarning`, [items]).toString();
  }

  errorOf(field: string): string {
    const text = typeof this.form[field] === "string" ? (this.form[field] as string) : "";
    if (field === "baseUrl" && !isValidPartnerAddress(text)) {
      return this.$t(`${PARTNERS}.fieldErrors.baseUrl`).toString();
    }
    if (field.startsWith("endpoints.") && !isValidPartnerEndpoint(text)) {
      return this.$t(`${PARTNERS}.fieldErrors.endpoint`).toString();
    }
    return "";
  }

  /**
   * @param clientId - the partner's owner, whose page this is.
   * @param partner - the partner as the page loaded it.
   * @param group - the card.
   */
  show(clientId: string, partner: ClientPartner, group: PartnerChangeGroup): Promise<PartnerDialogResult> {
    this.clientId = clientId;
    this.partner = partner;
    this.group = group;
    this.form = partnerForm(group, partner);
    this.baseDialog.show({
      title: this.$t(`${PARTNERS}.editTitles.${group}`, [partner.name]).toString(),
      positiveButtonText: this.$t("common.save").toString(),
      negativeButtonText: this.$t("common.close").toString(),
      maxWidth: 560,
      persistent: true,
      closeOnSave: false,
    });
    return new Promise((resolve) => (this.resolve = resolve));
  }

  /** SCHLIEßEN while nothing changed, ABBRECHEN after (edit-mode-dialogs.md). */
  @Watch("hasChanged")
  onHasChanged(hasChanged: boolean) {
    this.baseDialog.negativeButtonText = this.$t(hasChanged ? "common.abort" : "common.close").toString();
  }

  /** SCHLIEßEN has already closed the dialog; ABBRECHEN asks first. */
  async onNegative() {
    if (!this.hasChanged) {
      this.finish(null);
      return;
    }
    const discard = await this.confirmDialog.show({
      title: this.$t("common.confirmationDialog.unsavedChangesTitle").toString(),
      message: this.$t("common.confirmationDialog.unsavedChangesMessage").toString(),
      acceptMessage: this.$t("common.abort").toString(),
      rejectMessage: this.$t("common.back").toString(),
    });
    if (discard) {
      this.close(null);
    }
  }

  async onSave() {
    if (!this.canSave || !this.partner) {
      return;
    }
    this.busy = true;
    try {
      const saved = await updateClientPartner(this.clientId, this.partner.id, this.group, this.changes);
      SnackbarModule.showSuccess({ key: `${PARTNERS}.saved` });
      this.close(saved);
    } catch (error) {
      const key = partnerErrorKey(error);
      SnackbarModule.showError({ key });
      if (key.endsWith("CHANGED_MEANWHILE")) {
        this.close("CHANGED_MEANWHILE");
      }
    } finally {
      this.busy = false;
    }
  }

  close(result: PartnerDialogResult) {
    this.baseDialog.isShowing = false;
    this.finish(result);
  }

  private finish(result: PartnerDialogResult) {
    this.resolve?.(result);
    this.resolve = null;
  }
}
